import React, { FC, memo, useMemo, useCallback, FunctionComponent } from 'react'
import withTheme from '@sport1/react-elements/withTheme'
import { useImageSource } from '../../../utils/ImageHelper'
import { ThemeProps } from '../../../Theme/ThemeComponentTypes'
import { TestID } from '../../../utils/PropTypesDefinitions'
import BaseImage from '../BaseImage'
import { ImageBaseProps, ImageSizeProps } from '..'
import { ImageSizeTypes } from '../ImageInterfaces'

const getImageUrlWithoutPlaceholders = (imageUrl: string): string =>
    `${imageUrl.slice(0, imageUrl.lastIndexOf('/'))}`

const getNextImageSizes = (size: ImageSizeTypes): '33vw' | '50vw' | '100vw' => {
    if (size === 'S') {
        return '33vw'
    }
    if (size === 'M') {
        return '50vw'
    }
    return '100vw'
}

export type NextImageProps = ImageBaseProps & ImageSizeProps & TestID & ThemeProps

const NextImage: FC<NextImageProps> = ({
    imageUrl,
    alt,
    title,
    width = '100%',
    height,
    size,
    radiusType = 'none',
    aspectRatio = '16:9',
    resizeMode,
    theme,
    ignoreAspectRatio,
    webIsInFirstViewport = false,
}) => {
    const [imgSrc, setImgSrc] = React.useState(false)

    // Cast string values with px specified to number values
    const imageWidth = useMemo(
        () =>
            typeof width === 'string' && width.endsWith('px') ? +width.replace('px', '') : width,
        [width]
    )

    // Get imageData for defaultUrl and ratio values
    const [imageData] = useImageSource({
        width: imageWidth,
        size,
        aspectRatio,
        radiusType,
        imageUrl,
    })

    // Return same value if it's string or divide value by ratio when number
    const calculateHeight = useCallback(
        (ratio: number, width: string | number): string | number => {
            if (typeof width === 'string') {
                const h = parseFloat(width) / ratio
                const value = 0 === h % 1 ? h.toFixed() : h.toFixed(2)
                return `${value}%`
            }
            return Math.trunc(width / ratio)
        },
        []
    )

    // Get height value based on width and ratio
    const computeImageHeight = useCallback(
        ({
            widthValue,
            ratio,
        }: {
            widthValue: number | string
            ratio: number
        }): number | string => {
            let heightValue: number | string = '100%'
            if (typeof widthValue === 'number') {
                heightValue = calculateHeight(ratio, widthValue)
            }
            if (typeof widthValue === 'string') {
                heightValue = widthValue
            }
            return heightValue
        },
        [calculateHeight]
    )

    const imageHeight = useMemo(
        () =>
            computeImageHeight({
                widthValue: imageWidth,
                ratio: imageData.ratio,
            }),
        [computeImageHeight, imageData, imageWidth]
    )

    const isImageSizeNumerical = useMemo(
        () => typeof imageHeight === 'number' && typeof imageWidth === 'number',
        [imageHeight, imageWidth]
    )

    const getBaseImage = useCallback(
        style => {
            return (
                <div style={style}>
                    <BaseImage
                        {...{
                            alt,
                            aspectRatio,
                            height,
                            ignoreAspectRatio,
                            imageUrl,
                            radiusType,
                            resizeMode,
                            size,
                            theme,
                            width,
                        }}
                    />
                </div>
            )
        },
        [
            alt,
            aspectRatio,
            height,
            ignoreAspectRatio,
            imageUrl,
            radiusType,
            resizeMode,
            size,
            theme,
            width,
        ]
    )

    const getNextImage = useCallback(
        Image => {
            const imageProps = {
                alt,
                blurDataURL: `${imageUrl?.slice(0, imageUrl?.lastIndexOf('/'))}/10x10`,
                className: radiusType === 'rounded' ? 'rounded' : '',
                key: alt?.slice(0, Math.random() * 10),
                loader: ({ src, width }: { src: string; width: number }) => {
                    // Fallback Image
                    if (src.includes('.png')) {
                        return src
                    }

                    const imageSourceHeight = computeImageHeight({
                        widthValue: width,
                        ratio: imageData.ratio,
                    })

                    // The width value comes from next.config.js, to adjust the intrinsic value use the "sizes" property
                    return `${src}/${width}x${imageSourceHeight}`
                },
                onError: () => setImgSrc(true),
                placeholder: imageWidth < 40 ? 'empty' : 'blur',
                src: imgSrc
                    ? imageData.defaultUrl
                    : getImageUrlWithoutPlaceholders(String(imageUrl)),
                title,
            }

            const props = isImageSizeNumerical
                ? // Use layout intrinsic(default) when values are specified
                  {
                      ...imageProps,
                      width: imageWidth,
                      height: imageHeight,
                  }
                : // Use layout fill when values are string
                  {
                      ...imageProps,
                      layout: 'fill',
                      objectFit: resizeMode,
                      sizes: getNextImageSizes(size),
                  }

            return <Image {...props} />
        },
        [
            alt,
            computeImageHeight,
            imageData.defaultUrl,
            imageData.ratio,
            imageHeight,
            imageUrl,
            imageWidth,
            imgSrc,
            isImageSizeNumerical,
            radiusType,
            resizeMode,
            size,
            title,
        ]
    )

    const getImageStyle = useCallback(
        () => ({
            marginTop: isImageSizeNumerical
                ? undefined
                : `-${calculateHeight(imageData.ratio, imageWidth)}`,
        }),
        [calculateHeight, imageData.ratio, imageWidth, isImageSizeNumerical]
    )

    const getImageWrapStyle = useCallback(
        (): Record<string, string | number | undefined> => ({
            position: 'relative',
            width: isImageSizeNumerical ? `${imageWidth}px` : imageWidth,
            height: isImageSizeNumerical ? `${imageHeight}px` : undefined,
            paddingTop: isImageSizeNumerical
                ? undefined
                : `${calculateHeight(imageData.ratio, imageWidth)}`,
        }),
        [calculateHeight, imageData.ratio, imageHeight, imageWidth, isImageSizeNumerical]
    )

    let image
    if (webIsInFirstViewport) {
        image = getBaseImage(getImageStyle())
    } else {
        try {
            // eslint-disable-next-line @typescript-eslint/no-var-requires
            const Image = require('next/image').default
            image = getNextImage(Image)
        } catch {
            image = getBaseImage(getImageStyle())
        }
    }

    return <div style={getImageWrapStyle()}>{image}</div>
}

export default memo(withTheme(NextImage)) as FunctionComponent<NextImageProps>
